import React, { useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { useSelector } from 'react-redux';
import Popover from 'react-bootstrap/Popover';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { MultiSelect } from 'react-multi-select-component';
import moment from 'moment';
import { useCPDetailState } from './contextProvider/cpDetailProvider';
import DateFilter from './DateFilter';
import CustomCalendarPicker from './CustomCalendarPicker';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import Loader from '../../Components/Utility/Loader/Loader';
import { shortenandHoverString } from '../../Components/helper';
import './ColumnFilter.css';
import { CurrentYearItemRenderer } from '../../Components/Utility/CurrentYearDropdown';

function ColumnFilter(props) {
  const dateFormateFix = 'DD-MMM-YYYY';
  const reducerState = useSelector((redState) => redState);
  const {
    SystemMastersDetails: { customAttributeDetail },
  } = reducerState;
  const { detailState, keys, getTabColumn, setSelectedDays, columnsFilterData, setDetailState } = useCPDetailState();
  const { t } = useTranslation('common');
  const { columnElement, fetchFilterData, columnStateData, setColumnsFilterData, handleSelect, columnOptionLoader, allProductIds, filteredProductIds } = props;
  const [showPopover, setShowPopover] = useState(false);
  const appliedFilterColumn = columnsFilterData?.filter((item) => item.filterApplied).map((item) => item.columnId);
  const filterOptions = [
    {
      label: 'Exact',
      value: 'exact',
    },
    {
      label: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.NOT_FILLED'),
      value: 'notfilled',
    },
    {
      label: 'Before',
      value: 'before',
    },
    {
      label: 'After',
      value: 'after',
    },
    {
      label: 'Between',
      value: 'between',
    },
    {
      label: 'Tomorrow',
      value: 'tomorrow',
    },
    {
      label: 'Today',
      value: 'today',
    },
    {
      label: 'Yesterday',
      value: 'yesterday',
    },
    {
      label: 'Next Week',
      value: 'nextweek',
    },
    {
      label: 'This Week',
      value: 'thisweek',
    },
    {
      label: 'Last Week',
      value: 'lastweek',
    },
    {
      label: 'Next Month',
      value: 'nextmonth',
    },
    {
      label: 'Last Month',
      value: 'lastmonth',
    },
    {
      label: 'This Month',
      value: 'thismonth',
    },
    {
      label: 'Next Quarter',
      value: 'nextquarter',
    },
    {
      label: 'Last Quarter',
      value: 'lastquarter',
    },
    {
      label: 'This Quarter',
      value: 'thisquarter',
    },
    {
      label: 'Next Year',
      value: 'nextyear',
    },
    {
      label: 'Last Year',
      value: 'lastyear',
    },
    {
      label: 'This Year',
      value: 'thisyear',
    },
  ];
  const advanceFilterNeeded = ['exact', 'before', 'after', 'between'].includes(columnStateData?.filterBy);
  const isCalendarPickerNeeded = ['exact', 'before', 'after', 'between'].includes(columnStateData?.filterBy);
  const isRangeFilter = columnStateData?.filterBy === 'between';
  const disableFilterSection =
    (['before', 'after'].includes(columnStateData?.filterBy) && columnStateData?.selectedDays?.length >= 1) ||
    (['between'].includes(columnStateData?.filterBy) && columnStateData?.selectedDays?.length >= 2);
  const isBeforeAfterBetween = ['before', 'after', 'between'].includes(columnStateData?.filterBy);
  const columnKey = columnElement?.manualType?.key;
  const handleFilterIconClick = () => {
    if (columnKey !== 'Date_picker' && !showPopover) {
      fetchFilterData(columnElement);
    }
    setShowPopover(!showPopover);
  };
  const specialColumns = ['productName', 'poRef', 'location', 'skuNumber'];
  const handleColumnType = (columnId, colType, columnElement) => {
    if (specialColumns.includes(columnId)) {
      return 'po-based';
    }
    if (columnElement?.columnType === 'source') {
      return 'source';
    }
    return 'manual';
  };

  const resetAllFilters = () => {
    setShowPopover(false);
    const isProductNameFilterBeingReset = columnElement?._id === 'productName';
    const newFilters = columnsFilterData?.filter((item) => item.columnId !== columnElement?._id);
    setColumnsFilterData(newFilters);

    let params = {
      columnId: null,
    };

    const activeFilters = newFilters?.filter((item) => item.filterApplied) || [];

    if (activeFilters.length > 0) {
      const columnFilters = {};

      activeFilters.forEach((filter) => {
        const colElement = detailState?.columnsTab?.find((item) => item._id === filter.columnId);
        const colType = colElement?.manualType?.key;
        const columnType = handleColumnType(filter.columnId, colType, colElement);
        let sourceType = '';

        if (colElement?.columnType === 'source') {
          const customAttributeDetail = detailState?.customAttributeDetail || [];
          const isDropDown = customAttributeDetail?.find((item) => item._id === colElement?.source?.subLocation?.name);
          if (isDropDown?.type === 'dropdown' || isDropDown?.type === 'radio-button') {
            sourceType = 'dropdown';
          } else if (isDropDown?.type === 'date-picker') {
            sourceType = 'date-picker';
          }
        }

        const filterObj = {
          filterBy: filter.filterBy,
          selectedValues: filter.selectedDays || filter.selectedValues || [],
          columnType,
          fetchDetailFor: filter.filterType || 'actual',
          sourceType,
        };

        if (columnType === 'manual' && colType) {
          filterObj.manualTypeKey = colType;
        }

        columnFilters[filter.columnId] = filterObj;
      });

      params.columnFilters = columnFilters;

      const firstColElement = detailState?.columnsTab?.find((item) => item._id === activeFilters[0].columnId);
      const firstColType = firstColElement?.manualType?.key;
      params.columnId = activeFilters[0].columnId;
      params.columnType = handleColumnType(activeFilters[0].columnId, firstColType, firstColElement);
    }

    if (isProductNameFilterBeingReset) {
      params.resetProductNameFilter = true;
      if (activeFilters.length === 0) {
        params.filteredProductIds = [];
      }
    } else if (activeFilters.length === 0) {
      params.allProductIds = [];
    }

    const filterData = detailState?.filterData;
    if (filterData && keys.some((key) => Array.isArray(filterData[key]) && filterData[key].length > 0)) {
      const uiOnlyKeys = [
        'selectedColumnShow',
        'monthOptions',
        'yearOptions',
        'brandOptions',
        'category1Options',
        'category2Options',
        'category3Options',
        'category4Options',
        'productTypeOptions',
        'productStatusOptions',
        'seasonOptions',
        'supplierOptions',
        'createdByOption',
      ];
      const validFilters = Object.keys(filterData).reduce((acc, key) => {
        if (!uiOnlyKeys.includes(key) && Array.isArray(filterData[key]) && filterData[key].length > 0) {
          acc[key] = filterData[key];
        }
        return acc;
      }, {});
      params = { ...params, ...validFilters };
    }

    setDetailState((oldData) => ({
      ...oldData,
      tableLoading: true,
      tableColumData: [],
      tableProductDetail: [],
    }));
    getTabColumn(params);
  };

  const handleChangeMainFilter = async (selectedFilter) => {
    if (selectedFilter) {
      setColumnsFilterData((prev) => prev?.map((obj) => (obj.columnId === columnElement?._id ? { ...obj, filterBy: selectedFilter.value } : obj)));
    } else {
      setColumnsFilterData((prev) => prev?.map((obj) => (obj.columnId === columnElement?._id ? { ...obj, filterBy: null } : obj)));
    }
    setColumnsFilterData((prev) =>
      prev?.map((obj) =>
        obj.columnId === columnElement?._id
          ? {
              ...obj,
              selectedDays: [],
              selectedYear: obj.yearOptions?.length === 1 ? obj.yearOptions : [],
              selectedMonth: obj.monthOption?.length === 1 ? obj.monthOption : [],
              calendarDate: null,
              calendarStartDate: null,
              calendarEndDate: null,
            }
          : obj,
      ),
    );
    setSelectedDays([]);
    if (columnStateData?.yearOptions?.length === 1 && !isCalendarPickerNeeded) {
      setColumnsFilterData((prev) =>
        prev?.map((obj) => (obj.columnId === columnElement?._id ? { ...obj, selectedYear: [columnStateData?.yearOptions?.[0]] } : obj)),
      );
      await handleSelect([columnStateData?.yearOptions?.[0]], columnElement?._id, 'selectedYear');
    }
  };
  const handleCheckboxChange = (checkedValue, event) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      if (checkedValue === 'Not Filled') {
        setColumnsFilterData((prev) => prev?.map((obj) => (obj.columnId === columnElement?._id ? { ...obj, selectedValues: ['Not Filled'] } : obj)));
      } else {
        setColumnsFilterData((prev) =>
          prev?.map((obj) =>
            obj.columnId === columnElement?._id
              ? {
                  ...obj,
                  selectedValues: [...(obj.selectedValues || []).filter((val) => val !== 'Not Filled'), checkedValue],
                }
              : obj,
          ),
        );
      }
    } else {
      setColumnsFilterData((prev) =>
        prev?.map((obj) =>
          obj.columnId === columnElement?._id ? { ...obj, selectedValues: obj?.selectedValues?.filter((value) => value !== checkedValue) } : obj,
        ),
      );
    }
  };

  const buildFilterHierarchy = (selectedYears, originalData, selectedMonthValues = []) => {
    const result = [];
    selectedYears?.forEach(({ value: year }) => {
      const yearData = originalData[year];
      if (!yearData) return;
      const months = Object?.keys(yearData)?.filter((month) => !selectedMonthValues?.length || selectedMonthValues?.includes(month));
      if (months.length) {
        result.push({
          year,
          months: months?.map((month) => ({
            month,
            days: yearData[month],
          })),
        });
      }
    });

    return result;
  };
  const handleCalendarDateChange = (date) => {
    if (isRangeFilter) {
      if (Array.isArray(date) && date.length === 2) {
        const startDate = date[0] ? moment(date[0]).format(dateFormateFix) : null;
        const endDate = date[1] ? moment(date[1]).format(dateFormateFix) : null;
        const dates = [];
        if (startDate) dates.push(startDate);
        if (endDate) dates.push(endDate);
        setColumnsFilterData((prev) =>
          prev?.map((obj) =>
            obj.columnId === columnElement?._id
              ? {
                  ...obj,
                  selectedDays: dates,
                  calendarStartDate: date[0],
                  calendarEndDate: date[1],
                }
              : obj,
          ),
        );
        setSelectedDays(dates);
      }
    } else {
      const formattedDate = date ? moment(date).format(dateFormateFix) : null;
      const dates = formattedDate ? [formattedDate] : [];
      setColumnsFilterData((prev) =>
        prev?.map((obj) =>
          obj.columnId === columnElement?._id
            ? {
                ...obj,
                selectedDays: dates,
                calendarDate: date,
              }
            : obj,
        ),
      );
      setSelectedDays(dates);
    }
  };

  const applyColumnFilter = async () => {
    const { selectedDays, columnId, filterType, selectedValues } = columnStateData || {};
    let sortedDays = selectedDays;

    if (columnStateData?.filterBy === 'notfilled' || selectedValues === 'Not Filled') {
      sortedDays = [];
    } else if (columnKey === 'Date_picker') {
      if (!('filterBy' in columnStateData) || (advanceFilterNeeded && (!selectedDays || selectedDays.length === 0))) {
        toasterMsg(t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.DATE_VALIDATION'), 'danger');
        return;
      }

      if (columnStateData?.filterBy === 'between' && selectedDays?.length !== 2) {
        toasterMsg(t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.DATE_VALIDATION'), 'danger');
        return;
      }

      if (['exact', 'before', 'after'].includes(columnStateData?.filterBy) && selectedDays?.length !== 1) {
        toasterMsg(t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.DATE_VALIDATION'), 'danger');
        return;
      }
      if (columnStateData?.filterBy === 'between') {
        sortedDays = [...selectedDays].sort((a, b) => {
          const dateA = new Date(a.split('-').reverse().join('-'));
          const dateB = new Date(b.split('-').reverse().join('-'));
          return dateA - dateB;
        });
      }
    } else if (selectedValues === 0) {
      toasterMsg(t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.VALUE_REQUIRED'), 'danger');
      return;
    } else {
      sortedDays = selectedValues;

      if (columnKey === 'Number_Decimal' || columnKey === 'Number_Integer' || columnKey === 'Number_percentage') {
        if (Array.isArray(sortedDays)) {
          sortedDays = sortedDays.map((val) => {
            if (val === 'Not Filled' || val === null || val === undefined || val === '') {
              return val;
            }
            const num = parseFloat(val);
            return Number.isNaN(num) ? val : num;
          });
        } else if (sortedDays !== null && sortedDays !== undefined && sortedDays !== '') {
          const num = parseFloat(sortedDays);
          sortedDays = Number.isNaN(num) ? sortedDays : num;
        }
      }
    }
    let sourceType = '';
    if (columnElement?.columnType === 'source') {
      const isDropDown = customAttributeDetail?.find((item) => item._id === columnElement?.source?.subLocation?.name);
      if (isDropDown?.type === 'dropdown' || isDropDown?.type === 'radio-button') {
        sourceType = 'dropdown';
        sortedDays = customAttributeDetail?.filter((item) => sortedDays.includes(item?.keyName))?.map((e) => e?._id);
      } else if (isDropDown?.type === 'date-picker') {
        sourceType = 'date-picker';
      }
    }

    const isSpecialColumn = specialColumns.includes(columnId);
    let finalColumnType;
    if (isSpecialColumn) {
      finalColumnType = 'po-based';
    } else if (columnElement?.columnType === 'source') {
      finalColumnType = 'source';
    } else {
      finalColumnType = 'manual';
    }

    const isFilterAlreadyApplied = columnStateData?.filterApplied;

    let params = {
      sourceType,
      columnType: finalColumnType,
      fetchDetailFor: columnElement?.source?.subLocation?.name || filterType,
      selectedValues: sortedDays,
      filterBy: columnStateData?.filterBy,
    };

    if (!isFilterAlreadyApplied) {
      params.columnId = columnId;
    } else {
      params.columnId = null;
    }

    const allActiveFilters = columnsFilterData?.filter((item) => item.filterApplied) || [];

    if (allActiveFilters.length > 0 || !isFilterAlreadyApplied) {
      const columnFilters = {};

      allActiveFilters.forEach((filter) => {
        if (filter.columnId === columnId) {
          return;
        }
        const colElement = detailState?.columnsTab?.find((item) => item._id === filter.columnId);
        const colType = colElement?.manualType?.key;
        const columnType = handleColumnType(filter.columnId, colType, colElement);
        let filterSourceType = '';

        if (colElement?.columnType === 'source') {
          const customAttributeDetail = detailState?.customAttributeDetail || [];
          const isDropDown = customAttributeDetail?.find((item) => item._id === colElement?.source?.subLocation?.name);
          if (isDropDown?.type === 'dropdown' || isDropDown?.type === 'radio-button') {
            filterSourceType = 'dropdown';
          } else if (isDropDown?.type === 'date-picker') {
            filterSourceType = 'date-picker';
          }
        }

        const carrySelectedValues = filter.selectedDays || filter.selectedValues || [];
        let carryFetchDetailFor = filter.filterType || 'actual';
        let carryConvertedValues = carrySelectedValues;
        if (filterSourceType === 'dropdown' && colElement?.columnType === 'source') {
          carryFetchDetailFor = colElement?.source?.subLocation?.name || filter.filterType || 'actual';
          const carryAttrDetail = detailState?.customAttributeDetail || [];
          const converted = carryAttrDetail
            .filter((item) => carrySelectedValues.includes(item?.keyName))
            .map((e) => e?._id)
            .filter(Boolean);
          if (converted.length > 0) {
            carryConvertedValues = converted;
          }
        }
        const filterObj = {
          filterBy: filter.filterBy,
          selectedValues: carryConvertedValues,
          columnType,
          fetchDetailFor: carryFetchDetailFor,
          sourceType: filterSourceType,
        };

        if (columnType === 'manual' && colType) {
          filterObj.manualTypeKey = colType;
        }

        columnFilters[filter.columnId] = filterObj;
      });
      const currentFilterObj = {
        filterBy: columnStateData?.filterBy,
        selectedValues: sortedDays,
        columnType: finalColumnType,
        fetchDetailFor: columnElement?.source?.subLocation?.name || filterType,
        sourceType,
      };

      if (finalColumnType === 'manual' && columnKey) {
        currentFilterObj.manualTypeKey = columnKey;
      }

      columnFilters[columnId] = currentFilterObj;

      params.columnFilters = columnFilters;
    }

    if (allProductIds?.length > 0) {
      params.allProductIds = allProductIds;
    }

    const isProductNameFilterReset = columnId === 'productName' && (!sortedDays || sortedDays.length === 0) && columnStateData?.filterBy !== 'notfilled';

    if (isProductNameFilterReset) {
      params.filteredProductIds = [];
    } else if (filteredProductIds?.length > 0) {
      params.filteredProductIds = filteredProductIds;
    }
    const filterData = detailState?.filterData;
    if (filterData && keys.some((key) => Array.isArray(filterData[key]) && filterData[key].length > 0)) {
      const uiOnlyKeys = [
        'selectedColumnShow',
        'monthOptions',
        'yearOptions',
        'brandOptions',
        'category1Options',
        'category2Options',
        'category3Options',
        'category4Options',
        'productTypeOptions',
        'productStatusOptions',
        'seasonOptions',
        'supplierOptions',
        'createdByOption',
      ];
      const validFilters = Object.keys(filterData).reduce((acc, key) => {
        if (!uiOnlyKeys.includes(key) && Array.isArray(filterData[key]) && filterData[key].length > 0) {
          acc[key] = filterData[key];
        }
        return acc;
      }, {});
      params = { ...params, ...validFilters };
    }
    setColumnsFilterData((prev) => prev?.map((item) => (item?.columnId === columnElement?._id ? { ...item, filterApplied: !isFilterAlreadyApplied } : item)));
    setDetailState((prev) => ({
      ...prev,
      sortData: {
        sortAt: 'ProductInfo',
        sortBy: null,
        sortOrder: null,
      },
      tableLoading: true,
      tableColumData: [],
      tableProductDetail: [],
    }));
    setShowPopover(false);
    getTabColumn(params);
  };

  const handleSearch = (e) => {
    const targetValue = e.target.value;
    setColumnsFilterData((prev) =>
      prev?.map((obj) =>
        obj.columnId === columnElement?._id
          ? {
              ...obj,
              searchText: targetValue,
              textFilterData: (obj.textOptions || []).filter((item) =>
                String(item.value ?? '')
                  .toLowerCase()
                  .includes(targetValue.toLowerCase()),
              ),
            }
          : obj,
      ),
    );
  };

  const selectedMonthValues = columnStateData?.selectedMonth?.map((m) => m.value);
  const filteredData = buildFilterHierarchy(columnStateData?.selectedYear, columnStateData?.originalData, selectedMonthValues);
  const isFilterbyRequired = columnKey === 'Date_picker' ? !columnStateData?.filterBy : false;
  const isFilterTypeRequired = columnKey === 'Date_picker' ? !columnStateData?.filterType : false;
  const isBetweenConditionMatched = columnStateData?.filterBy === 'between' ? columnStateData?.selectedDays?.length === 2 : true;
  const isSingleDateConditionMatched = ['exact', 'before', 'after'].includes(columnStateData?.filterBy) ? columnStateData?.selectedDays?.length === 1 : true;

  let calendarValue = null;
  let calendarStartDate = null;
  let calendarEndDate = null;

  if (isRangeFilter) {
    calendarStartDate =
      columnStateData?.calendarStartDate || (columnStateData?.selectedDays?.[0] ? moment(columnStateData.selectedDays[0], dateFormateFix).toDate() : null);
    calendarEndDate =
      columnStateData?.calendarEndDate || (columnStateData?.selectedDays?.[1] ? moment(columnStateData.selectedDays[1], dateFormateFix).toDate() : null);
  } else {
    calendarValue =
      columnStateData?.calendarDate || (columnStateData?.selectedDays?.[0] ? moment(columnStateData.selectedDays[0], dateFormateFix).toDate() : null);
  }

  return (
    <OverlayTrigger
      placement={t('TAKING_DATES.BUTTON')}
      trigger="click"
      onEnter={() => handleFilterIconClick()}
      overlay={
        <Popover className={`${!columnElement?.isPinned && 'hide-behind'} custom-popover cp-manual-filter`}>
          <Popover.Content>
            {columnOptionLoader ? (
              <Loader />
            ) : (
              <>
                {columnKey === 'Date_picker' ? (
                  <>
                    <div className="radio-group-container mb-8px">
                      <p className="custom-radio">
                        <input
                          type="radio"
                          id={`actual${columnElement?._id}`}
                          onChange={() => fetchFilterData(columnElement, 'actual')}
                          checked={columnStateData?.filterType === 'actual'}
                          name={`filterDateType${columnElement?._id}`}
                          disabled={columnStateData?.filterApplied}
                        />
                        <label htmlFor={`actual${columnElement?._id}`} className="f-14">
                          {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.ACTUAL')}
                        </label>
                      </p>
                      <p className="custom-radio">
                        <input
                          type="radio"
                          id={`planned${columnElement?._id}`}
                          onChange={() => fetchFilterData(columnElement, 'planned')}
                          checked={columnStateData?.filterType === 'planned'}
                          name={`filterDateType${columnElement?._id}`}
                          disabled={columnStateData?.filterApplied}
                        />
                        <label htmlFor={`planned${columnElement?._id}`} className="f-14">
                          {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.PLANNED')}
                        </label>
                      </p>
                    </div>
                    <div className="mb-8 common-react-select">
                      <p className="field-name f-14 height-18">{t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.FILTER_BY')}</p>
                      <Select
                        name="filterBy"
                        options={filterOptions}
                        getOptionLabel={(option) => `${option.label}`}
                        getOptionValue={(option) => `${option.value}`}
                        maxMenuHeight={140}
                        value={filterOptions?.find((option) => option.value === columnStateData?.filterBy) || null}
                        className="common-select"
                        classNamePrefix="common-select"
                        onChange={(e) => handleChangeMainFilter(e)}
                        placeholder="Select Filter"
                        isDisabled={!columnStateData?.filterType || columnStateData?.filterApplied}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="search-container">
                      <i className="icon-Magnifying-Glass-Solid" />
                      <input
                        type="text"
                        className="form-control"
                        value={columnStateData?.searchText}
                        placeholder={t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SEARCH')}
                        onChange={(e) => handleSearch(e)}
                        disabled={columnStateData?.filterApplied}
                      />
                    </div>
                    <p className="f-12 mb-0">{t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.COLUMN_VALUE')}</p>
                    <div className="mb-8 text-options-container">
                      {columnStateData?.textFilterData?.length > 0 ? (
                        columnStateData?.textFilterData?.map((option, index) => {
                          const isNotFilledSelected = columnStateData?.selectedValues?.includes('Not Filled');
                          const hasOtherSelections = columnStateData?.selectedValues?.some((val) => val !== 'Not Filled');
                          const isCurrentOptionNotFilled = option.value === 'Not Filled';

                          const isDisabled =
                            columnStateData?.filterApplied ||
                            (isNotFilledSelected && !isCurrentOptionNotFilled) ||
                            (hasOtherSelections && isCurrentOptionNotFilled);

                          return (
                            <div key={option} className="common-checkbox size-xsm mb-8">
                              <input
                                id={`check_${index}_${option.value}_text`}
                                type="checkbox"
                                checked={columnStateData?.selectedValues?.includes(option.value)}
                                onChange={(event) => handleCheckboxChange(option.value, event)}
                                disabled={isDisabled}
                              />
                              <label htmlFor={`check_${index}_${option.value}_text`} className={`chklabel ${isDisabled ? 'disabled' : ''}`}>
                                <span>{shortenandHoverString(option?.label, 16) || null}</span>
                              </label>
                            </div>
                          );
                        })
                      ) : (
                        <p className="f-12 text-center mb-0">{t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.NO_DATA_FOUND')}</p>
                      )}
                    </div>
                  </>
                )}
                {isCalendarPickerNeeded ? (
                  <div className="mb-8">
                    <p className="field-name f-14 mb-0 text-black font-medium">{t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SELECT_DATE')}:</p>
                    <CustomCalendarPicker
                      isRange={isRangeFilter}
                      value={calendarValue}
                      startDate={calendarStartDate}
                      endDate={calendarEndDate}
                      placeholder={
                        isRangeFilter
                          ? t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SELECT_DATE_RANGE') || 'Select Date Range'
                          : t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SELECT_DATE') || 'Select Date'
                      }
                      onChange={handleCalendarDateChange}
                      disabled={columnStateData?.filterApplied}
                    />
                  </div>
                ) : (
                  advanceFilterNeeded && (
                    <>
                      <MultiSelect
                        name="year"
                        disabled={columnStateData?.filterApplied}
                        ItemRenderer={CurrentYearItemRenderer}
                        options={columnStateData?.yearOptions || []}
                        value={columnStateData?.selectedYear || []}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        labelledBy={
                          t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT_GRADING_GROUP')
                        }
                        overrideStrings={{
                          selectSomeItems: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SELECT_YEAR'),
                          search: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SELECT_YEAR'),
                        }}
                        ClearSelectedIcon={null}
                        onChange={(selected) => {
                          handleSelect(selected, columnElement?._id, 'selectedYear');
                        }}
                      />
                      <MultiSelect
                        name="month"
                        options={columnStateData?.monthOption || []}
                        value={columnStateData?.selectedMonth || []}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        labelledBy={
                          t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT_GRADING_GROUP')
                        }
                        overrideStrings={{
                          selectSomeItems: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SELECT_MONTH'),
                          search: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SELECT_MONTH'),
                        }}
                        ClearSelectedIcon={null}
                        disabled={columnStateData?.filterApplied}
                        onChange={(selected) => {
                          handleSelect(selected, columnElement?._id, 'selectedMonth');
                        }}
                      />
                      {columnStateData?.selectedMonth?.length > 0 && columnStateData?.selectedYear?.length > 0 && (
                        <DateFilter
                          disableFilterSection={disableFilterSection}
                          isBeforeAfterBetween={isBeforeAfterBetween}
                          filteredData={filteredData}
                          columnId={columnElement?._id}
                        />
                      )}
                    </>
                  )
                )}

                <div className="filter-btn-container">
                  <button type="button" onClick={resetAllFilters} disabled={isFilterTypeRequired} className="btn btn-reset">
                    {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.RESET')}
                  </button>
                  <button
                    type="button"
                    onClick={applyColumnFilter}
                    disabled={
                      (columnKey !== 'Date_picker' &&
                        columnStateData?.filterBy !== 'notfilled' &&
                        (!columnStateData?.textFilterData || columnStateData?.textFilterData?.length === 0)) ||
                      (isFilterTypeRequired && columnStateData?.filterBy !== 'notfilled') ||
                      (isFilterbyRequired && columnStateData?.filterBy !== 'notfilled') ||
                      !isBetweenConditionMatched ||
                      !isSingleDateConditionMatched ||
                      columnStateData?.filterApplied
                    }
                    className="btn btn-apply"
                  >
                    {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.APPLY')}
                  </button>
                </div>
              </>
            )}
          </Popover.Content>
        </Popover>
      }
    >
      <button
        type="button"
        aria-label="Filter"
        className={`btn d-inline-flex p-4pix cp-column-filter-btn${columnStateData?.filterApplied ? ' active' : ''}`}
        disabled={
          (!appliedFilterColumn?.includes(columnElement?._id) && appliedFilterColumn?.length === 5) ||
          detailState?.tableColumData?.length === 0 ||
          detailState?.loading ||
          detailState?.isMainLoading ||
          detailState?.tableLoading
        }
      >
        <i className="icon-Filter f-24" />
      </button>
    </OverlayTrigger>
  );
}

export default ColumnFilter;
